/**
 * FE read layer for the `timelineItemStatusV1` projection (People/Timeline tab v2).
 *
 * Instead of loading every full ~68 KB `experiencesV1` doc and iterating them, the
 * tab reads these compact per-instance projections (built by the BE denormalizer)
 * and computes the same pills + per-participant rows. Gated by the `PEOPLE_TAB_V2`
 * flag with a live-instance fallback (see design_spec_timeline_item_status.md).
 *
 * The projection type + collection name are defined LOCALLY (stable wire strings)
 * so this compiles against the currently-published @dadohr/shared; swap to the
 * shared `FS.timelineItemStatus` / `useFeatureEnabled('PEOPLE_TAB_V2')` once the
 * republished shared package is picked up.
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationActionStatus,
  AutomationStatus,
  ExperienceStatus,
  IExperience,
  MessageStatus,
  SubTaskStatus,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { mapValues, uniqBy } from 'lodash';
import moment from 'moment-timezone';

import { PARTICIPANT_ACTOR_KEY } from '@dadohr/shared/domainTypes/experience';

import { useFeatureEnabled } from '../features';
import {
  store,
  useCombineLoadingValues3,
  useMappedLoadingValue
} from '../firebase';
import {
  CollectionListener,
  createCollectionListenerStore,
  useCollectionListener
} from '../firecache/collectionListener';
import { tsFromMillis } from '../time';
import { ExperienceStatsSimple } from './instance';

// ---------------------------------------------------------------------------
// Projection shape (mirror of BE ITimelineItemStatus — kept local to avoid a
// hard dependency on a not-yet-published @dadohr/shared).
// ---------------------------------------------------------------------------
export type DerivedMessageStatus =
  | 'OK'
  | 'ERROR'
  | 'BOUNCED'
  | 'UPCOMING'
  | 'SKIPPED'
  | 'UNKNOWN';

export interface TimelineItemStatusTask {
  templateId: string;
  actor: string;
  status: TaskStatus;
  completedLate: boolean;
  completedAt: Timestamp | null;
  // `end` = the task's fixed-window deadline; the Schedule/People tabs derive
  // display-only lateness from it (now > end && not COMPLETE/SKIPPED) because the
  // BE only promotes a task to LATE on its dedicated sweep.
  timing: { start: Timestamp | null; end: Timestamp | null };
  subTasks: { templateId: string; status: SubTaskStatus }[];
}

export interface TimelineItemStatusAutomation {
  templateId: string;
  status: AutomationStatus;
  timing: { at: Timestamp | null };
  actions: { templateId: string; status: AutomationActionStatus }[];
}

export interface TimelineItemStatusMessage {
  status: DerivedMessageStatus;
  at: Timestamp | null;
  sentAt: Timestamp | null;
}

export interface TimelineItemStatusProjection {
  id: string;
  orgId: string;
  templateId: string;
  instanceId: string;
  participantUserId: string;
  actors: { key: string; userId: string }[];
  startsAt: Timestamp | null;
  launchAt: Timestamp | null;
  expectedEnd: Timestamp | null;
  createdAt: Timestamp | null;
  manuallyArchivedAt: Timestamp | null;
  experienceStatus: ExperienceStatus;
  anchorDate: { at: Timestamp | null } | null;
  tasks: TimelineItemStatusTask[];
  automations: { [tplId: string]: TimelineItemStatusAutomation };
  messages: { [msgTplId: string]: TimelineItemStatusMessage };
  updatedAt: Timestamp;
}

const TIMELINE_ITEM_STATUS_COLLECTION = 'timelineItemStatusV1';
const DELIMITER = '@@@';

// ---------------------------------------------------------------------------
// Feature flag — idiomatic shared registry lookup. Requires @dadohr/shared to
// include PEOPLE_TAB_V2 in the Feature union + FEATURES map (dbKey 'peopleTabV2',
// defaultValue false); bump the FE's shared dependency to the republished version.
// ---------------------------------------------------------------------------
export const usePeopleTabV2Enabled = (): boolean => useFeatureEnabled('PEOPLE_TAB_V2');

// ---------------------------------------------------------------------------
// Listeners
//
// The projection is read two ways:
//   1. ALL docs for a template (timelineItemStatusStore) — the "all time"
//      Participants view, which intentionally wants every enrollment ever.
//   2. Currently-relevant only — the People/Schedule tabs + Overview counts.
//      This MUST filter SERVER-SIDE. Fetching every lifetime doc and filtering
//      in JS streams a template's entire enrollment history over the single
//      Firestore WebChannel (profiled on a heavy org: ~25k docs for ~1k
//      currently-relevant across the cards), which is both slow and a
//      regression vs the V1 experiencesV1 path. So we mirror
//      useExperienceInstancesCurrentlyRelevant's three server-filtered legs
//      (Firestore allows one inequality per query):
//        - upcoming:     experienceStatus == NOT_STARTED           AND startsAt    <= now+3wk
//        - ongoing:      experienceStatus IN (RUNNING,LATE,COMPLETE) AND expectedEnd > now-threshold
//        - recentlyMade: experienceStatus IN (RUNNING,LATE,COMPLETE) AND createdAt   > now-threshold
//      then dedup + apply the exact isProjectionArchived bound client-side.
//      Needs composite indexes on (orgId, experienceStatus, templateId,
//      {startsAt|expectedEnd|createdAt}); see firestore.indices.json.
// ---------------------------------------------------------------------------

const ONGOING_STATUSES = [
  ExperienceStatus.RUNNING,
  ExperienceStatus.LATE,
  ExperienceStatus.COMPLETE
];

const projectionCollection = () =>
  store().collection(TIMELINE_ITEM_STATUS_COLLECTION);

// (1) ALL docs for a template — "all time" Participants view (no relevance filter).
const timelineItemStatusStore = createCollectionListenerStore<TimelineItemStatusProjection>(
  (key) => {
    const [orgId, tplId] = key.split(DELIMITER);
    return new CollectionListener(
      projectionCollection()
        .where('orgId', '==', orgId)
        .where('templateId', '==', tplId)
    );
  }
);

// (2a) Upcoming leg: NOT_STARTED instances starting within the 3-week window.
const upcomingStore = createCollectionListenerStore<TimelineItemStatusProjection>(
  (key) => {
    const [orgId, tplId, minStart] = key.split(DELIMITER);
    return new CollectionListener(
      projectionCollection()
        .where('orgId', '==', orgId)
        .where('templateId', '==', tplId)
        .where('experienceStatus', '==', ExperienceStatus.NOT_STARTED)
        .where('startsAt', '<=', tsFromMillis(parseInt(minStart, 10)))
    );
  }
);

// (2b) Ongoing leg (expectedEnd): keeps normal ongoing instances relevant.
const ongoingByMaxEndStore = createCollectionListenerStore<TimelineItemStatusProjection>(
  (key) => {
    const [orgId, tplId, maxEnd] = key.split(DELIMITER);
    return new CollectionListener(
      projectionCollection()
        .where('orgId', '==', orgId)
        .where('templateId', '==', tplId)
        .where('experienceStatus', 'in', ONGOING_STATUSES)
        .where('expectedEnd', '>', tsFromMillis(parseInt(maxEnd, 10)))
    );
  }
);

// (2c) Ongoing leg (createdAt): surfaces LATE/backdated enrollments whose
// expectedEnd is already past but which were created recently.
const ongoingByMinCreatedAtStore = createCollectionListenerStore<TimelineItemStatusProjection>(
  (key) => {
    const [orgId, tplId, minCreatedAt] = key.split(DELIMITER);
    return new CollectionListener(
      projectionCollection()
        .where('orgId', '==', orgId)
        .where('templateId', '==', tplId)
        .where('experienceStatus', 'in', ONGOING_STATUSES)
        .where('createdAt', '>', tsFromMillis(parseInt(minCreatedAt, 10)))
    );
  }
);

// Mirror of the BE archival single-source-of-truth (shared archived.ts →
// getComputedArchiveDate/isInstanceArchived, ported to the FE instance.ts by the
// "late enrollee not autoarchive" change): an instance is archived when it's
// manually archived OR now is past max(expectedEnd, createdAt) + expectedEndThreshold.
// The createdAt leg is the late/backdated-enrollment fix — a backdated anchor puts
// expectedEnd in the past, so counting the grace window from createdAt (when it is the
// later of the two) keeps a late enrollee relevant instead of archiving it on create.
// Boundary matches tsIsAfter (strict >): archived only once now passes the archive date.
const isProjectionArchived = (
  d: TimelineItemStatusProjection,
  expectedEndThreshold: number,
  nowMs: number
): boolean => {
  if (d.manuallyArchivedAt) return true;
  if (!d.expectedEnd) return false;
  const baseMs =
    d.createdAt && d.createdAt.toMillis() > d.expectedEnd.toMillis()
      ? d.createdAt.toMillis()
      : d.expectedEnd.toMillis();
  return baseMs + expectedEndThreshold < nowMs;
};

/**
 * Currently-relevant projection docs for a template — the projection-backed
 * equivalent of useExperienceInstancesCurrentlyRelevant. Mirrors its three
 * server-filtered legs (see the Listeners note) then dedups and applies the
 * exact isProjectionArchived bound, so we never stream a template's full
 * enrollment history just to compute the relevant set.
 */
export const useTimelineItemStatuses = (
  orgId: string,
  tplId: string,
  expectedEndThreshold: number,
  n: moment.Moment = moment()
) => {
  // Day-granular lower bound = startOfDay(now) - threshold, mirroring
  // useExperienceInstancesCurrentlyRelevant. Both ongoing legs share it (the
  // createdAt leg reuses maxEnd); the isProjectionArchived post-filter then
  // tightens both legs to the exact archive bound.
  const maxEnd = n
    .clone()
    .startOf('d')
    .subtract(expectedEndThreshold, 'milliseconds');
  const minStart = n.clone().startOf('d').add(3, 'weeks');

  const upcomingKey = [orgId, tplId, `${minStart.valueOf()}`].join(DELIMITER);
  const ongoingKey = [orgId, tplId, `${maxEnd.valueOf()}`].join(DELIMITER);
  const recentlyCreatedKey = [orgId, tplId, `${maxEnd.valueOf()}`].join(
    DELIMITER
  );

  return useMappedLoadingValue(
    useCombineLoadingValues3(
      useCollectionListener(upcomingStore(upcomingKey)),
      useCollectionListener(ongoingByMaxEndStore(ongoingKey)),
      useCollectionListener(ongoingByMinCreatedAtStore(recentlyCreatedKey))
    ),
    ([upcoming, ongoing, recentlyCreated]) => {
      const nowMs = n.valueOf();
      // The legs overlap (a normal ongoing instance matches both the expectedEnd
      // and createdAt legs), so dedup by id before the archival post-filter.
      return uniqBy(
        upcoming.concat(ongoing).concat(recentlyCreated),
        (x) => x.id
      ).filter((r) => !isProjectionArchived(r.data, expectedEndThreshold, nowMs));
    }
  );
};

// ---------------------------------------------------------------------------
// projectionToPseudoInstance — reconstruct a PARTIAL Doc<IExperience> from a
// projection so the EXISTING People-tab components/stat functions render
// unchanged (the flag only swaps the data SOURCE, not the components).
//
// Faithful for everything the tables/pills read: task/automation/subtask/action
// status, item timing, completedAt, anchorDate.at, actor identities (name
// resolution via useUserMapById), and message status — the last via a FAKED
// concrete `messages[]` log rebuilt from the precomputed status + sentAt, so
// `getFullMessageStatusIncludingMostRecentConcreteMessageStatus` returns the
// same value. It does NOT reconstruct interpolated content / error payloads /
// QUESTION answers — those live only in row EXPAND, which lazy-loads the one
// full instance when the flag is on.
// ---------------------------------------------------------------------------
const derivedToRawMessageStatus = (s: DerivedMessageStatus): MessageStatus =>
  s === 'UPCOMING'
    ? MessageStatus.UPCOMING
    : s === 'SKIPPED'
    ? MessageStatus.SKIPPED
    :s === 'ERROR'
    ? MessageStatus.ERROR
    : MessageStatus.DONE; // OK/ERROR/BOUNCED/UNKNOWN → DONE; the concrete log decides

export const projectionToPseudoInstance = (
  p: Doc<TimelineItemStatusProjection>
): Doc<IExperience> => {
  const d = p.data;
  const messagesStatuses: Record<string, unknown> = {};
  const concrete: unknown[] = [];
  Object.entries(d.messages).forEach(([msgId, m]) => {
    messagesStatuses[msgId] = {
      status: derivedToRawMessageStatus(m.status),
      audience: '',
      timing: { at: m.at, manualOverride: false }
    };
    if (m.status === 'OK' || m.status === 'ERROR' || m.status === 'BOUNCED') {
      concrete.push({
        provider: 'POSTMARK_EMAIL',
        type: 'EXPERIENCE_MESSAGE',
        msgId,
        status: m.status,
        scope: { msgId, taskIds: [] },
        at: m.sentAt ?? m.at,
        error: null,
        ignore: false
      });
    }
  });

  return ({
    id: p.id,
    collection: p.collection,
    data: {
      id: p.id,
      orgId: d.orgId,
      templateId: d.templateId,
      status: d.experienceStatus,
      startsAt: d.startsAt,
      launchAt: d.launchAt,
      expectedEnd: d.expectedEnd,
      createdAt: d.createdAt,
      manuallyArchivedAt: d.manuallyArchivedAt,
      anchorDate: d.anchorDate,
      userIds: d.actors.map((a) => a.userId).filter(Boolean),
      actors: d.actors.map((a) => ({ key: a.key, userId: a.userId, type: 'USER', accessToken: '' })),
      tasks: d.tasks.map((t) => ({
        id: t.templateId,
        templateId: t.templateId,
        actor: t.actor,
        status: t.status,
        completedLate: t.completedLate,
        completedAt: t.completedAt,
        timing: { start: t.timing.start, end: t.timing.end },
        subTasks: t.subTasks.map((s) => ({ id: s.templateId, templateId: s.templateId, status: s.status }))
      })),
      automations: mapValues(d.automations, (a) => ({
        templateId: a.templateId,
        status: a.status,
        timing: { at: a.timing.at },
        actions: a.actions
      })),
      messagesStatuses,
      messages: concrete
    }
  } as unknown) as Doc<IExperience>;
};

/** Currently-relevant projections mapped to pseudo-instances — a drop-in for
 *  `useExperienceInstancesCurrentlyRelevant` when PEOPLE_TAB_V2 is on. */
export const useTimelineItemPseudoInstances = (
  orgId: string,
  tplId: string,
  expectedEndThreshold: number,
  n: moment.Moment = moment()
) =>
  useMappedLoadingValue(useTimelineItemStatuses(orgId, tplId, expectedEndThreshold, n), (rows) =>
    rows.map(projectionToPseudoInstance)
  );

/** ALL projections for a template (no currently-relevant filter) as pseudo-instances
 *  — drop-in for `useExperienceInstancesByTemplateId` (Participants "all time" view). */
export const useTimelineItemAllTimePseudoInstances = (orgId: string, tplId: string) =>
  useMappedLoadingValue(
    useCollectionListener(timelineItemStatusStore([orgId, tplId].join(DELIMITER))),
    (rows) => rows.map(projectionToPseudoInstance)
  );

/**
 * Participant enrollment stats computed DIRECTLY from projections — a byte-for-byte
 * equivalent of collectExperienceStatsForActor (instance.ts) that skips building a
 * full pseudo-instance per doc. A count only needs each instance's status + its actor
 * task statuses, both already on the projection, so for a template with a large
 * currently-relevant set this avoids ~10x the per-doc work of projectionToPseudoInstance
 * (no messagesStatuses / fake concrete messages[] / subtask+automation reconstruction).
 * The logic mirrors collectExperienceStatsForActor EXACTLY — including areAllTasksDone's
 * empty-array => done (`every` on an empty list is true) and the onTrack/notOnTrack
 * second pass over ALL tasks — so the numbers are identical to the pseudo-instance path.
 * Only the count uses this; People/Schedule/Participants still build pseudo-instances,
 * and row-expand still lazy-loads the full instance.
 */
const collectStatsFromProjections = (
  rows: Doc<TimelineItemStatusProjection>[],
  actor: string
): ExperienceStatsSimple => {
  const s: ExperienceStatsSimple = {
    total: 0,
    done: 0,
    todo: 0,
    overdue: 0,
    upcoming: 0,
    onTrack: 0,
    notOnTrack: 0
  };

  rows.forEach((r) => {
    const d = r.data;
    s.total++;
    if (d.experienceStatus === ExperienceStatus.NOT_STARTED) {
      s.upcoming++;
      return;
    }
    const actorTasks = d.tasks.filter((t) => t.actor === actor);
    if (actorTasks.some((t) => t.status === TaskStatus.LATE)) {
      s.overdue++;
      return;
    }
    // areAllTasksDone: every task COMPLETE — an empty actorTasks array is `true` => done.
    if (actorTasks.every((t) => t.status === TaskStatus.COMPLETE)) {
      s.done++;
      return;
    }
    if (actorTasks.some((t) => t.status === TaskStatus.RUNNING)) {
      s.todo++;
    } else {
      s.done++;
    }
  });

  rows.forEach((r) => {
    const d = r.data;
    if (d.experienceStatus === ExperienceStatus.NOT_STARTED) {
      return;
    }
    const allTaskCount = d.tasks.length;
    const lateTaskCount = d.tasks.filter((t) => t.status === TaskStatus.LATE)
      .length;
    if (!allTaskCount) {
      s.onTrack++;
      return;
    }
    if (lateTaskCount > 3 && lateTaskCount / allTaskCount > 0.2) {
      s.notOnTrack++;
    } else {
      s.onTrack++;
    }
  });

  return s;
};

/** Participant enrollment stats from projections — drop-in for
 *  `useInstanceStatsByTemplateId` (which also scopes to PARTICIPANT_ACTOR_KEY). */
export const useTimelineItemStatsByTemplateId = (
  orgId: string,
  tplId: string,
  expectedEndThreshold: number,
  n: moment.Moment = moment()
) =>
  useMappedLoadingValue(useTimelineItemStatuses(orgId, tplId, expectedEndThreshold, n), (rows) =>
    collectStatsFromProjections(rows, PARTICIPANT_ACTOR_KEY)
  );
